import React, { createContext, useContext, useEffect, useMemo, useState } from "react"

type Eligibility = "all" | "Eligible" | "Ineligible" | "Failed" | "Pending"

export type ComplianceFilters = {
  searchQuery: string
  locationFilter: string
  sheriffTypeFilter: string
  driverManagerTeamFilter: string
  dspFilter: string
  eligibilityFilter: Eligibility
}

type ComplianceFiltersContextValue = ComplianceFilters & {
  setSearchQuery: (v: string) => void
  setLocationFilter: (v: string) => void
  setSheriffTypeFilter: (v: string) => void
  setDriverManagerTeamFilter: (v: string) => void
  setDspFilter: (v: string) => void
  setEligibilityFilter: (v: Eligibility) => void
  resetFilters: () => void
}

const defaultFilters: ComplianceFilters = {
  searchQuery: "",
  locationFilter: "all",
  sheriffTypeFilter: "all",
  driverManagerTeamFilter: "all",
  dspFilter: "all",
  eligibilityFilter: "all",
}

const STORAGE_KEY = "compliance-shared-filters"

const ComplianceFiltersContext = createContext<ComplianceFiltersContextValue | undefined>(undefined)

export const ComplianceFiltersProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const readStored = (): ComplianceFilters => {
    if (typeof window === "undefined") return defaultFilters
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      if (!raw) return defaultFilters
      const parsed = JSON.parse(raw) as Partial<ComplianceFilters>
      return {
        ...defaultFilters,
        ...parsed,
      }
    } catch {
      return defaultFilters
    }
  }

  const [filters, setFilters] = useState<ComplianceFilters>(readStored)

  useEffect(() => {
    if (typeof window === "undefined") return
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
  }, [filters])

  const value = useMemo<ComplianceFiltersContextValue>(() => {
    return {
      ...filters,
      setSearchQuery: (v) => setFilters((prev) => ({ ...prev, searchQuery: v })),
      setLocationFilter: (v) => setFilters((prev) => ({ ...prev, locationFilter: v })),
      setSheriffTypeFilter: (v) => setFilters((prev) => ({ ...prev, sheriffTypeFilter: v })),
      setDriverManagerTeamFilter: (v) =>
        setFilters((prev) => ({ ...prev, driverManagerTeamFilter: v })),
      setDspFilter: (v) => setFilters((prev) => ({ ...prev, dspFilter: v })),
      setEligibilityFilter: (v) => setFilters((prev) => ({ ...prev, eligibilityFilter: v })),
      resetFilters: () => setFilters(defaultFilters),
    }
  }, [filters])

  return (
    <ComplianceFiltersContext.Provider value={value}>{children}</ComplianceFiltersContext.Provider>
  )
}

export const useComplianceFilters = (): ComplianceFiltersContextValue => {
  const ctx = useContext(ComplianceFiltersContext)
  if (!ctx) {
    throw new Error("useComplianceFilters must be used within ComplianceFiltersProvider")
  }
  return ctx
}
